import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";

import {
  HiOutlineDocumentText,
  HiOutlineMagnifyingGlass,
  HiOutlinePencilSquare,
  HiOutlineTrash,
  HiOutlinePlus,
  HiOutlineXMark,
  HiOutlinePhoto,
  HiOutlinePaperClip,
  HiOutlineCheckCircle,
  HiOutlineArrowPath,
  HiOutlineCloudArrowUp,
  HiOutlineFolderOpen,
} from "react-icons/hi2";

const API_URL = process.env.REACT_APP_URL_PREFIX || "";

const LIST_URL = `${API_URL}msg/drm_documents`;

const itemUrl = (id) => `${API_URL}msg/drm_documents/${id}`;

const emptyForm = {
  category: "",
  type: "doc",
  title: "",
  description: "",
};

const gradient =
  "linear-gradient(135deg, #063b87 0%, #0a57bb 50%, #087344 100%)";

// Handles common API response structures
const getRecords = (response) => {
  const data = response.data;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.messages)) return data.messages;
  if (Array.isArray(data?.records)) return data.records;

  return [];
};

const getThumbnail = (item) => {
  const value = item?.thumbnail_url || item?.thumbnail || item?.thumbnails;

  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value[0]?.url || value[0]?.path || "";
  if (value && typeof value === "object") return value.url || value.path || "";

  return "";
};

const getId = (item) => item?.id ?? item?.message_id ?? item?._id;

export default function FileUpdate({
  listEndpoint = LIST_URL,
  getItemEndpoint = itemUrl,
  onSuccess,
  onDelete,
}) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const [selectedItem, setSelectedItem] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [documents, setDocuments] = useState([]);

  // --------------------------------
  // Fetch records
  // --------------------------------

  const fetchRecords = async () => {
    try {
      setLoading(true);

      const response = await axios.get(listEndpoint);

      setRecords(getRecords(response));
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to load files",
        text: error.response?.data?.message || error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, [listEndpoint]);

  // --------------------------------
  // Thumbnail preview
  // --------------------------------

  useEffect(() => {
    if (!thumbnail) return;

    const url = URL.createObjectURL(thumbnail);

    setThumbnailPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [thumbnail]);

  // --------------------------------
  // Filtering
  // --------------------------------

  const categories = useMemo(
    () => [...new Set(records.map((item) => item.category).filter(Boolean))],
    [records],
  );

  const filteredRecords = useMemo(() => {
    return records.filter((item) => {
      const query = search.toLowerCase();

      const matchesSearch = [
        item.category,
        item.type,
        item.message,
        item.description,
        getId(item),
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(query),
      );

      const matchesCategory =
        categoryFilter === "all" || item.category === categoryFilter;

      const matchesType = typeFilter === "all" || item.file_type === typeFilter;

      return matchesSearch && matchesCategory && matchesType;
    });
  }, [records, search, categoryFilter, typeFilter]);

  // --------------------------------
  // Open edit form
  // --------------------------------

  const openEdit = (item) => {
    setSelectedItem(item);

    setForm({
      category: item.category || "",
      type: item.type || "doc",
      title: item.title || "",
      description: item.description || "",
    });

    setThumbnail(null);
    setThumbnailPreview(getThumbnail(item));
    setDocuments([]);
  };

  const closeEdit = () => {
    setSelectedItem(null);
    setForm(emptyForm);
    setThumbnail(null);
    setThumbnailPreview(null);
    setDocuments([]);
  };

  const change = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // --------------------------------
  // Update record
  // --------------------------------

  const updateRecord = async (e) => {
    e.preventDefault();

    if (!form.category.trim()) {
      return Swal.fire({
        icon: "warning",
        title: "category required",
        text: "Please enter the message.",
      });
    }

    const confirm = await Swal.fire({
      title: "Update this record?",
      text: "Your changes will be saved.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update",
      confirmButtonColor: "#0a57bb",
    });

    if (!confirm.isConfirmed) return;

    const body = new FormData();

    Object.entries(form).forEach(([key, value]) => {
      body.append(key, value ?? "");
    });

    if (thumbnail) {
      body.append("thumbnails", thumbnail);
    }

    documents.forEach((file) => {
      body.append("documents", file);
    });

    try {
      setSaving(true);

      const id = getId(selectedItem);

      const response = await axios.put(getItemEndpoint(id), body);

      await Swal.fire({
        icon: "success",
        title: "Updated successfully",
        text: response.data?.message || "Record updated successfully.",
        confirmButtonColor: "#0a57bb",
      });

      closeEdit();
      await fetchRecords();

      onSuccess?.(response.data);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Update failed",
        text: error.response?.data?.message || error.message,
      });
    } finally {
      setSaving(false);
    }
  };

  // --------------------------------
  // Delete record
  // --------------------------------

  const deleteRecord = async (item) => {
    const id = getId(item);

    const confirm = await Swal.fire({
      title: "Delete this file?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete permanently",
      confirmButtonColor: "#dc3545",
    });

    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);

      const response = await axios.delete(
        `${API_URL}msg/delete_drm_documents/${id}`,
      );

      setRecords((prev) => prev.filter((record) => getId(record) !== id));

      if (getId(selectedItem) === id) {
        closeEdit();
      }

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: response.data?.message || "Record deleted successfully.",
        confirmButtonColor: "#0a57bb",
      });

      onDelete?.(id, response.data);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text: error.response?.data?.message || error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------
  // Shared input style
  // --------------------------------

  const labelClass = "form-label fw-semibold text-secondary";

  // --------------------------------
  // Render
  // --------------------------------

  return (
    <div className="container-fluid py-3 px-2 px-md-4">
      {/* =====================================
          HEADER
      ===================================== */}

      <div
        className="rounded-4 p-3 p-md-4 text-white mb-4 shadow-sm"
        style={{ background: gradient }}
      >
        <div className="d-flex align-items-center gap-3">
          <div
            className="rounded-3 d-flex align-items-center justify-content-center"
            style={{
              width: 50,
              height: 50,
              background: "rgba(255,255,255,0.16)",
              fontSize: 27,
            }}
          >
            <HiOutlineFolderOpen />
          </div>

          <div className="flex-grow-1">
            <h5 className="fw-bold mb-1">Update Files</h5>
            <small className="text-white text-opacity-75">
              Manage, search, edit and organize your files
            </small>
          </div>

          <span className="badge bg-white bg-opacity-25 border border-white border-opacity-25 rounded-pill px-3 py-2">
            {records.length} RECORDS
          </span>
        </div>
      </div>

      {/* =====================================
          EDIT FORM
      ===================================== */}

      {selectedItem && (
        <div
          className="card border-0 rounded-4 mb-4 overflow-hidden"
          style={{
            boxShadow: "0 5px 25px rgba(6,59,135,0.10)",
          }}
        >
          <div
            className="d-flex align-items-center justify-content-between p-3 text-white"
            style={{ background: gradient }}
          >
            <div>
              <h6 className="fw-bold mb-1">
                <HiOutlinePencilSquare className="me-2" />
                Edit File / Message
              </h6>
              <small>Record ID: {getId(selectedItem)}</small>
            </div>

            <button
              type="button"
              className="btn btn-sm text-white border rounded-circle"
              onClick={closeEdit}
            >
              <HiOutlineXMark size={19} />
            </button>
          </div>

          <form onSubmit={updateRecord}>
            <div className="card-body p-3 p-md-4">
              <div className="row g-3">
                <div className="col-md">
                  <label className="form-label fw-semibold text-secondary">
                    <i className="bi bi-grid me-2 text-primary" />
                    Name
                  </label>
                  <input
                    className="form-control form-control rounded-3"
                    name="title"
                    value={form.title}
                    onChange={change}
                    placeholder="Enter Title"
                  />
                </div>
                <div className="col-md">
                  <label className={labelClass}>Category</label>

                  <input
                    className="form-control rounded-3"
                    name="category"
                    value={form.category}
                    onChange={change}
                  />
                </div>

                <div className="col-md">
                  <label className={labelClass}>Type</label>

                  <input
                    className="form-control rounded-3"
                    name="type"
                    value={form.type}
                    onChange={change}
                  />
                </div>

                <div className="col-12">
                  <label className={labelClass}>Description</label>

                  <textarea
                    className="form-control rounded-3"
                    rows="2"
                    name="description"
                    value={form.description}
                    onChange={change}
                  />
                </div>

                {/* Thumbnail */}

                <div className="col-md-6">
                  <label className={labelClass}>
                    <HiOutlinePhoto className="me-1" />
                    Thumbnail
                  </label>

                  <input
                    id="editThumbnail"
                    className="form-control rounded-3"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];

                      if (file && !file.type.startsWith("image/")) {
                        Swal.fire(
                          "Invalid file",
                          "Please select an image.",
                          "warning",
                        );
                        e.target.value = "";
                        return;
                      }

                      setThumbnail(file || null);
                    }}
                  />

                  {thumbnailPreview && (
                    <div
                      className="position-relative mt-3 border rounded-3 overflow-hidden bg-light"
                      style={{ height: 190 }}
                    >
                      <img
                        src={thumbnailPreview}
                        alt="Thumbnail preview"
                        className="w-100 h-100"
                        style={{ objectFit: "contain" }}
                      />

                      <button
                        type="button"
                        className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2 rounded-circle"
                        onClick={() => {
                          setThumbnail(null);
                          setThumbnailPreview(null);

                          const input =
                            document.getElementById("editThumbnail");
                          if (input) input.value = "";
                        }}
                      >
                        <HiOutlineXMark />
                      </button>
                    </div>
                  )}
                </div>

                {/* Documents */}

                <div className="col-md-6">
                  <label className={labelClass}>
                    <HiOutlinePaperClip className="me-1" />
                    Add Documents
                  </label>

                  <input
                    className="form-control rounded-3"
                    type="file"
                    multiple
                    onChange={(e) =>
                      setDocuments(Array.from(e.target.files || []))
                    }
                  />

                  <small className="text-muted d-block mt-2">
                    Newly selected documents will be uploaded.
                  </small>

                  {documents.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="small text-success mt-2 text-break"
                    >
                      <HiOutlineCheckCircle className="me-1" />
                      {file.title || "title"}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="card-footer bg-white p-3 d-flex justify-content-between flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-outline-danger rounded-3"
                onClick={() => deleteRecord(selectedItem)}
                disabled={saving}
              >
                <HiOutlineTrash className="me-2" />
                Delete Record
              </button>

              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-light border rounded-3"
                  onClick={closeEdit}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn text-white rounded-3 px-4"
                  style={{ background: gradient }}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <HiOutlineCloudArrowUp className="me-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* =====================================
          FILTERS
      ===================================== */}

      <div className="card border-0 rounded-4 mb-4 shadow-sm">
        <div className="card-body p-3">
          <div className="row g-2 align-items-center">
            <div className="col-lg-5">
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <HiOutlineMagnifyingGlass size={19} />
                </span>

                <input
                  type="search"
                  className="form-control border-start-0"
                  placeholder="Search messages, category, ID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="col-sm-6 col-lg-3">
              <select
                className="form-select"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">All Categories</option>

                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-sm-6 col-lg-2">
              <select
                className="form-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="all">All File type</option>
                {[...new Set(records.map((r) => r.file_type))].map((r, i) => (
                  <option key={i} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-lg-2">
              <button
                className="btn btn-light border w-100"
                onClick={() => {
                  setSearch("");
                  setCategoryFilter("all");
                  setTypeFilter("all");
                }}
              >
                <HiOutlineArrowPath className="me-2" />
                Clear Filters
              </button>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <small className="text-muted">
              Showing <strong>{filteredRecords.length}</strong> of{" "}
              <strong>{records.length}</strong> records
            </small>

            <button
              className="btn btn-sm btn-outline-primary rounded-3"
              onClick={fetchRecords}
              disabled={loading}
            >
              <HiOutlineArrowPath className="me-1" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* =====================================
          FILE LIST
      ===================================== */}

      {loading && records.length === 0 ? (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: "#0a57bb" }} />
          <p className="text-muted mt-2">Loading records...</p>
        </div>
      ) : filteredRecords.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body text-center py-5">
            <HiOutlineFolderOpen size={50} className="text-secondary mb-3" />
            <h6 className="fw-bold">No records found</h6>
            <p className="text-muted small mb-0">
              Try changing your search or filter.
            </p>
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {filteredRecords.map((item) => {
            const id = getId(item);
            const image = getThumbnail(item);

            return (
              <div
                key={id}
                className="card border-0 rounded-4 overflow-hidden"
                style={{
                  boxShadow: "0 3px 14px rgba(6,59,135,0.07)",
                  transition: "0.2s ease",
                }}
              >
                <div className="card-body p-3">
                  <div className="d-flex align-items-center justify-content-between gap-3">
                    {/* Thumbnail */}
                    <div
                      className="flex-shrink-0 rounded-3 overflow-hidden bg-light d-flex align-items-center justify-content-center"
                      style={{
                        width: 100,
                        height: 85,
                      }}
                    >
                      {image ? (
                        <img
                          src={image}
                          alt={item.title || "File thumbnail"}
                          className="w-100 h-100"
                          style={{ objectFit: "contain" }}
                        />
                      ) : (
                        <HiOutlineDocumentText
                          size={38}
                          className="text-secondary"
                        />
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-grow-1 min-width-0">
                      <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                        <span
                          className="badge rounded-pill text-white px-3 py-2"
                          style={{ background: gradient }}
                        >
                          {item.category || "Uncategorized"}
                        </span>

                        <span className="badge bg-light text-secondary border rounded-pill px-3 py-2">
                          {item.type || "doc"}
                        </span>

                        <small className="text-muted">ID: {id}</small>
                      </div>

                      <h6
                        className="fw-bold mb-1"
                        style={{
                          color: "#063b87",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {item.title || "Untitled"}
                      </h6>
                    </div>
                    <div>
                      {" "}
                      <p
                        className="text-muted small mb-0"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item.description || "No description available."}
                      </p>
                    </div>
                    {/* Actions */}
                    <div className="d-flex gap-2 flex-shrink-0">
                      {/* <button
                        className="btn btn-sm text-white rounded-3 px-3"
                        style={{ background: gradient }}
                        onClick={() => openEdit(item)}
                      >
                        <HiOutlinePencilSquare className="me-1" />
                        Edit
                      </button> */}

                      <button
                        className="btn btn-sm btn-outline-danger rounded-3 px-3"
                        onClick={() => deleteRecord(item)}
                        disabled={loading}
                      >
                        <HiOutlineTrash className="me-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
